import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { ChevronLeft, ChevronRight, Megaphone, Pin, Play, FileText, Download } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const resolveUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return u.startsWith("/api/") ? `${base}${u}` : `${API}${u.startsWith("/") ? "" : "/"}${u}`;
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "";
  }
};

// Get the best cover for a post: image_url first, then first image in media
const getCover = (p) => {
  if (p.image_url) return p.image_url;
  const firstImage = (p.media || []).find((m) => m.kind === "image");
  return firstImage?.url || "";
};

export default function PostsFeed() {
  const [posts, setPosts] = useState([]);
  const [active, setActive] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/posts").then(({ data }) => setPosts(data)).catch(() => setPosts([]));
  }, []);

  if (!posts.length) return null;

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.85), behavior: "smooth" });
  };

  const mediaCount = (p) => (p.media || []).length + (p.image_url && !(p.media || []).some((m) => m.url === p.image_url) ? 1 : 0);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12" data-testid="posts-feed">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="inline-flex items-center gap-2 text-[#B45309] mb-2">
            <Megaphone className="h-4 w-4" />
            <span className="text-xs uppercase tracking-[0.2em]">Sanstha Updates</span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl text-[#5C061C]">Today's Updates</h2>
        </div>
        <div className="hidden sm:flex gap-2">
          <button onClick={() => scroll(-1)} aria-label="Scroll left" data-testid="posts-scroll-left" className="h-10 w-10 rounded-full border border-[#E8DEC8] bg-[#FAF7F2] text-[#5C061C] hover:bg-[#F4EEDF] grid place-items-center transition"><ChevronLeft className="h-4 w-4" /></button>
          <button onClick={() => scroll(1)} aria-label="Scroll right" data-testid="posts-scroll-right" className="h-10 w-10 rounded-full border border-[#E8DEC8] bg-[#FAF7F2] text-[#5C061C] hover:bg-[#F4EEDF] grid place-items-center transition"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>

      <div ref={scrollRef} className="flex gap-5 overflow-x-auto pb-4 snap-x snap-mandatory scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {posts.map((p) => {
          const cover = getCover(p);
          const count = mediaCount(p);
          return (
            <button type="button" key={p.id} onClick={() => setActive(p)} data-testid={`post-card-${p.id}`} className="snap-start shrink-0 w-[85%] sm:w-[420px] text-left card-warm rounded-2xl overflow-hidden hover:-translate-y-1 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D97706]">
              <div className="aspect-[16/10] bg-[#F4EEDF] relative">
                {cover ? (
                  <img src={resolveUrl(cover)} alt={p.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#F4EEDF] via-[#FAF3E0] to-[#F9E4B7] grid place-items-center">
                    <span className="font-serif text-5xl text-[#5C061C]/40">ॐ</span>
                  </div>
                )}
                {p.pinned && (
                  <span className="absolute top-3 left-3 bg-[#D97706] text-white text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full shadow-md inline-flex items-center gap-1">
                    <Pin className="h-3 w-3" /> Pinned
                  </span>
                )}
                {count > 1 && (
                  <span className="absolute bottom-3 right-3 bg-black/60 text-white text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full">
                    {count} media
                  </span>
                )}
              </div>
              <div className="p-5">
                <p className="text-xs uppercase tracking-[0.2em] text-[#B45309]">{fmtDate(p.date || p.created_at)}</p>
                <h3 className="font-serif text-xl text-[#5C061C] mt-2 line-clamp-2">{p.title}</h3>
                {p.description && (
                  <p className="mt-2 text-sm text-[#6E6259] leading-relaxed line-clamp-4 whitespace-pre-wrap">{p.description}</p>
                )}
                {p.author_name && <p className="mt-3 text-xs text-[#6E6259]">— {p.author_name}</p>}
                <p className="mt-3 text-xs font-semibold text-[#D97706] inline-flex items-center gap-1">Read more →</p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Post detail popup */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="bg-[#FAF7F2] w-[95vw] max-w-3xl p-0 gap-0 block overflow-y-auto overscroll-contain max-h-[90vh] rounded-2xl" data-testid="post-detail-dialog">
          <DialogTitle className="sr-only">{active?.title || "Sanstha Post"}</DialogTitle>
          <DialogDescription className="sr-only">{active?.description ? active.description.slice(0, 140) : "Sanstha announcement details"}</DialogDescription>
          {active && (() => {
            const allMedia = [
              ...(active.image_url && !(active.media || []).some((m) => m.url === active.image_url)
                ? [{ id: "cover", kind: "image", url: active.image_url }]
                : []),
              ...(active.media || []),
            ];
            const heroImage = allMedia.find((m) => m.kind === "image");
            return (
              <>
                {heroImage ? (
                  <div className="bg-[#F4EEDF]" data-testid="post-detail-image">
                    <img src={resolveUrl(heroImage.url)} alt={active.title} className="w-full h-auto block" />
                  </div>
                ) : (
                  <div className="aspect-[16/9] bg-gradient-to-br from-[#F4EEDF] via-[#FAF3E0] to-[#F9E4B7] grid place-items-center">
                    <span className="font-serif text-7xl text-[#5C061C]/40">ॐ</span>
                  </div>
                )}
                <div className="p-6">
                  <p className="text-xs uppercase tracking-[0.2em] text-[#B45309]">{fmtDate(active.date || active.created_at)}</p>
                  <h3 className="font-serif text-3xl text-[#5C061C] mt-2" data-testid="post-detail-title">{active.title}</h3>
                  {active.description ? (
                    <p className="mt-4 text-[#3F3A34] leading-relaxed whitespace-pre-wrap" data-testid="post-detail-description">{active.description}</p>
                  ) : (
                    <p className="mt-4 text-sm italic text-[#6E6259]" data-testid="post-detail-description-empty">No additional details for this post.</p>
                  )}
                  {active.cta_label && active.cta_url && (
                    <a
                      href={active.cta_url}
                      target={active.cta_url.startsWith("http") ? "_blank" : "_self"}
                      rel="noopener noreferrer"
                      data-testid="post-cta-button"
                      className="mt-5 inline-flex items-center gap-2 btn-saffron rounded-full px-6 h-11 font-semibold text-sm"
                    >
                      {active.cta_label} →
                    </a>
                  )}
                  {allMedia.length > 0 && (
                    <div className="mt-6">
                      <p className="text-xs uppercase tracking-[0.2em] text-[#B45309] mb-3">Attachments · {allMedia.length}</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {allMedia.map((m, idx) => (
                          <div key={m.id || idx} data-testid={`post-media-${idx}`} className="card-warm rounded-xl overflow-hidden relative">
                            {m.kind === "image" && (
                              <button type="button" onClick={() => setLightbox(m)} className="block w-full aspect-square">
                                <img src={resolveUrl(m.url)} alt={m.caption || ""} className="w-full h-full object-cover hover:scale-105 transition" />
                              </button>
                            )}
                            {m.kind === "video" && (
                              <button type="button" onClick={() => setLightbox(m)} className="block w-full aspect-square bg-[#1F1A17] relative">
                                <video src={resolveUrl(m.url)} className="w-full h-full object-cover opacity-70" muted playsInline />
                                <div className="absolute inset-0 grid place-items-center">
                                  <div className="h-12 w-12 rounded-full bg-white/90 grid place-items-center"><Play className="h-5 w-5 text-[#5C061C]" /></div>
                                </div>
                              </button>
                            )}
                            {m.kind === "pdf" && (
                              <a href={resolveUrl(m.url)} target="_blank" rel="noopener noreferrer" className="block w-full aspect-square bg-gradient-to-br from-[#F4EEDF] to-[#F9E4B7] grid place-items-center hover:from-[#F9E4B7] hover:to-[#F4EEDF] transition p-4">
                                <FileText className="h-10 w-10 text-[#5C061C]" />
                                <span className="mt-2 text-xs font-semibold text-[#5C061C] inline-flex items-center gap-1"><Download className="h-3 w-3" /> Open PDF</span>
                              </a>
                            )}
                            {m.caption && <p className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[10px] p-1.5 truncate">{m.caption}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {active.author_name && (
                    <p className="mt-6 text-sm text-[#6E6259] italic">— {active.author_name}</p>
                  )}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Lightbox for images / videos */}
      {lightbox && (
        <div className="fixed inset-0 bg-black/90 z-[70] flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="post-lightbox">
          <div className="max-w-5xl w-full max-h-[92vh]" onClick={(e) => e.stopPropagation()}>
            {lightbox.kind === "image" ? (
              <img src={resolveUrl(lightbox.url)} alt="" className="max-h-[88vh] mx-auto rounded-xl" />
            ) : (
              <video src={resolveUrl(lightbox.url)} controls autoPlay className="max-h-[88vh] w-full rounded-xl bg-black" />
            )}
            {lightbox.caption && <p className="text-center text-white mt-3">{lightbox.caption}</p>}
            <div className="text-center mt-3"><button onClick={() => setLightbox(null)} className="bg-white/90 text-[#5C061C] px-4 py-2 rounded-lg font-semibold">Close</button></div>
          </div>
        </div>
      )}
    </section>
  );
}
